import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Template {
  id: string;
  name: string;
  category: string;
  content: string;
  createdAt: Date;
  lastModified: Date;
  usageCount: number;
}

interface TemplateContextType {
  templates: Template[];
  addTemplate: (template: Omit<Template, 'id' | 'createdAt' | 'lastModified' | 'usageCount'>) => void;
  updateTemplate: (id: string, template: Partial<Template>) => void;
  deleteTemplate: (id: string) => void;
}

const TemplateContext = createContext<TemplateContextType | undefined>(undefined);

export const useTemplates = () => {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error('useTemplates must be used within a TemplateProvider');
  }
  return context;
};

interface TemplateProviderProps {
  children: ReactNode;
}

export const TemplateProvider: React.FC<TemplateProviderProps> = ({ children }) => {
  // Initial templates data
  const [templates, setTemplates] = useState<Template[]>([
    {
      id: '1',
      name: 'Welcome Message',
      category: 'Marketing',
      content: 'Hi {name}, welcome to our service! We\'re excited to have you on board...',
      createdAt: new Date('2023-01-15'),
      lastModified: new Date('2023-01-15'),
      usageCount: 1234
    },
    {
      id: '2',
      name: 'Order Confirmation',
      category: 'Notifications',
      content: 'Thank you for your order #{order_number}. Your order has been confirmed and will be delivered soon.',
      createdAt: new Date('2023-02-20'),
      lastModified: new Date('2023-03-10'),
      usageCount: 856
    },
    {
      id: '3',
      name: 'Appointment Reminder',
      category: 'Reminders',
      content: 'Reminder: You have an appointment scheduled for {appointment_date} at {appointment_time}.',
      createdAt: new Date('2023-03-05'),
      lastModified: new Date('2023-03-05'),
      usageCount: 2103
    },
    {
      id: '4',
      name: 'Special Offer',
      category: 'Marketing',
      content: 'Special offer for {name}! Get 20% off on your next purchase. Use code: SPECIAL20',
      createdAt: new Date('2023-04-12'),
      lastModified: new Date('2023-04-12'),
      usageCount: 567
    }
  ]);

  const addTemplate = (templateData: Omit<Template, 'id' | 'createdAt' | 'lastModified' | 'usageCount'>) => {
    const newTemplate: Template = {
      ...templateData,
      id: Date.now().toString(),
      createdAt: new Date(),
      lastModified: new Date(),
      usageCount: 0
    };
    
    setTemplates(prevTemplates => [...prevTemplates, newTemplate]);
  };

  const updateTemplate = (id: string, templateData: Partial<Template>) => {
    setTemplates(prevTemplates => 
      prevTemplates.map(template => 
        template.id === id 
          ? { 
              ...template, 
              ...templateData, 
              lastModified: new Date() 
            } 
          : template
      )
    );
  };

  const deleteTemplate = (id: string) => {
    setTemplates(prevTemplates => prevTemplates.filter(template => template.id !== id));
  };

  return (
    <TemplateContext.Provider value={{ templates, addTemplate, updateTemplate, deleteTemplate }}>
      {children}
    </TemplateContext.Provider>
  );
}; 